/**
 * AfterDonationSection.tsx - Post-Donation Behavior Configuration
 *
 * This component configures what happens after a successful donation including
 * redirect options, thank you messages, and follow-up actions.
 *
 * Key Features:
 * - Post-donation redirect configuration
 * - Thank you message customization
 * - Follow-up action settings
 * - Email receipt configuration
 * - Social sharing encouragement
 * - Additional donation prompts
 *
 * Redirect Options:
 * - Stay on Page: Keep donor on fundraiser page
 * - Thank You Page: Redirect to custom thank you page
 * - External URL: Redirect to organization website
 * - Other Fundraisers: Suggest related fundraisers
 *
 * Configuration Settings:
 * - Redirect URL validation and testing
 * - Custom thank you message text
 * - Email receipt template customization
 * - Social sharing button configuration
 * - Additional donation encouragement
 *
 * Follow-up Actions:
 * - Automatic email receipts
 * - Newsletter signup prompts
 * - Social media sharing encouragement
 * - Volunteer opportunity suggestions
 * - Recurring donation setup
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, URL validation, message templates
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, X, Check, ChevronDown, Plus } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { FundraiserSelector } from "../../shared/FundraiserSelector";
import { AdvancedDropdown } from "../../shared/AdvancedDropdown";
import {
  FundRaiserRedirectType,
  FundRaiserStatus,
  RedirectOtherFundraiserType,
} from "../../../enums";
import { CreateFundraiserModel } from "../../../models";
import PreviewIcon from "../../../images/fundraiser-preview-icon.svg";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 10;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const AccountCard = styled.div<{ selected: boolean }>`
  padding: 20px;
  border: 2px solid ${(props) => (props.selected ? "#3b82f6" : "#e2e8f0")};
  border-radius: 12px;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  width: 50%;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const AccountHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const AccountName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`;

const AccountDetails = styled.div`
  color: #64748b;
  font-size: 14px;
  line-height: 1.4;
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

const URLInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 50%;
`;

const PreviewButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const MobilePageWithPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 50%;
`;

const MobilePageButton = styled.button<{ isOpen: boolean; hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: #ffffff;
  color: ${(props) => (props.hasValue ? "#1e293b" : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ChevronIcon = styled(ChevronDown) <{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  color: #64748b;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const MobilePageDropdownContainer = styled.div`
  position: relative;
  width: calc(100% - 44px);
`;

const MobilePageDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10000;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const MobilePageSearchContainer = styled.div`
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const MobilePageSearchIcon = styled(Search)`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #94a3b8;
`;

const MobilePageSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const MobilePageOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.isSelected ? "#f0f9ff" : "transparent")};
  color: #374151;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#f1f5f9")};
  }

  &:focus {
    outline: none;
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#e0f2fe")};
  }
`;

const SelectedCheckmark = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const SelectedBadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  width: 100%;
`;

const SelectedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #0ea5e9;
`;

const BadgeRemoveButton = styled.button`
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(3, 105, 161, 0.1);
  }
`;

const MobilePagePreviewButton = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const DropdownCaptionText = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-top: 12px;
  line-height: 1.4;
  font-style: italic;
  width: 50%;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

interface AfterDonationSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
}

export const AfterDonationSection: React.FC<AfterDonationSectionProps> =
  observer(({ builderData, updateBuilderField }) => {
    const [showMobilePageDropdown, setShowMobilePageDropdown] = useState(false);
    const [mobilePageSearchTerm, setMobilePageSearchTerm] = useState("");
    const [showFundraiserSelector, setShowFundraiserSelector] = useState(false);
    const mobilePageDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          mobilePageDropdownRef.current &&
          !mobilePageDropdownRef.current.contains(event.target as Node)
        ) {
          setShowMobilePageDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const activeFundraisers = fundraiserStore.fundraisers.filter(
      (f) => f.status === FundRaiserStatus.Active && f.id !== builderData.id
    );

    const isValidUrl = (url: string): boolean => {
      if (!url) return true;

      const originalUrl = url.trim();
      if (originalUrl === "") return true;

      let processedUrl = originalUrl;
      let startsWithWww = false;

      if (originalUrl.startsWith("www.")) {
        startsWithWww = true;
      }

      if (
        !originalUrl.startsWith("http://") &&
        !originalUrl.startsWith("https://")
      ) {
        processedUrl = `https://${originalUrl}`;
      }

      try {
        const urlObj = new URL(processedUrl);
        const hostname = urlObj.hostname;

        if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
          return false;
        }

        if (
          hostname === "localhost" ||
          /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
        ) {
          return true;
        }

        const domainRegex =
          /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
        if (!domainRegex.test(hostname)) {
          return false;
        }

        const dotCount = hostname.split(".").length - 1;
        if (startsWithWww) {
          return dotCount >= 2;
        } else {
          return dotCount >= 1;
        }
      } catch (e) {
        return false;
      }
    };

    const handleFundraiserSelectionChange = (fundraiserIds: string[]) => {
      updateBuilderField("redirectSpecificFundraiserIds", fundraiserIds);
    };

    const removeFundraiserFromSelection = (fundraiserId: string) => {
      const current = builderData.redirectSpecificFundraiserIds || [];
      const updated = current.filter((id: string) => id !== fundraiserId);
      updateBuilderField("redirectSpecificFundraiserIds", updated);
    };

    const handleWebsitePreview = () => {
      if (
        builderData.redirectWebsiteUrl &&
        isValidUrl(builderData.redirectWebsiteUrl)
      ) {
        let url = builderData.redirectWebsiteUrl;
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = `https://${url}`;
        }
        window.open(url, "_blank");
      }
    };

    const mobilePages = fundraiserStore.mobilePages;

    const handleMobilePagePreview = () => {
      if (builderData.redirectMobilePage) {
        const selectedPage = mobilePages.find(
          (p) => p.id === builderData.redirectMobilePage
        );
        if (!selectedPage) return;
        window.open(selectedPage.url, "_blank");
      }
    };

    const getFilteredMobilePages = () => {
      return mobilePages.filter((page) =>
        page.name.toLowerCase().includes(mobilePageSearchTerm.toLowerCase())
      );
    };

    const clearMobilePageSearch = () => {
      setMobilePageSearchTerm("");
    };

    // Auto-open modal if specific fundraisers is selected but no fundraisers are selected
    // React.useEffect(() => {
    //   if (
    //     builderData.redirectOtherFundraiserType ===
    //       RedirectOtherFundraiserType.Specific &&
    //     (!builderData.redirectSpecificFundraiserIds ||
    //       builderData.redirectSpecificFundraiserIds.length === 0)
    //   ) {
    //     setShowFundraiserSelector(true);
    //   }
    // }, [builderData.redirectOtherFundraiserType]);

    return (
      <Section>
        <SectionTitle>Options</SectionTitle>
        <FormGroup>
          <div style={{ marginBottom: "16px" }}>
            <Label style={{ marginBottom: "16px" }}>
              Redirect fundraiser if unavailable
            </Label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "32px",
                margin: "18px 0 8px 0",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#374151",
                  fontWeight: "400",
                }}
              >
                <input
                  type="radio"
                  name="redirectFundraiserUnavailable"
                  value="yes"
                  checked={
                    builderData.redirectOnVisit &&
                    builderData.redirectOnVisit !== FundRaiserRedirectType.None
                  }
                  onChange={() => {
                    updateBuilderField("redirectOnVisit", "other-fundraisers");
                  }}
                  style={{
                    width: "18px",
                    height: "18px",
                    margin: "0",
                    cursor: "pointer",
                    accentColor: "#3b82f6",
                  }}
                />
                Yes
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#374151",
                  fontWeight: "400",
                }}
              >
                <input
                  type="radio"
                  name="redirectFundraiserUnavailable"
                  value="no"
                  checked={
                    !builderData.redirectOnVisit ||
                    builderData.redirectOnVisit === FundRaiserRedirectType.None
                  }
                  onChange={() => {
                    updateBuilderField(
                      "redirectOnVisit",
                      FundRaiserRedirectType.None
                    );
                    updateBuilderField("redirectSpecificFundraiserIds", []);
                    updateBuilderField("redirectWebsiteUrl", "");
                    updateBuilderField("redirectMobilePage", "page1");
                  }}
                  style={{
                    width: "18px",
                    height: "18px",
                    margin: "0",
                    cursor: "pointer",
                    accentColor: "#3b82f6",
                  }}
                />
                No
              </label>
            </div>

            {/* Show redirect options when Yes is selected */}
            {builderData.redirectOnVisit &&
              builderData.redirectOnVisit !== FundRaiserRedirectType.None && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    width: "75%",
                    marginTop: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    <select
                      style={{
                        width: "50%",
                        padding: "12px 16px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "16px",
                        color: "#1e293b",
                        background: "#ffffff",
                        cursor: "pointer",
                        transition: "border-color 0.2s ease",
                      }}
                      value={builderData.redirectOtherFundraiserType}
                      onChange={(e) => {
                        updateBuilderField(
                          "redirectOtherFundraiserType",
                          e.target.value
                        );
                        if (e.target.value !== "specific") {
                          updateBuilderField(
                            "redirectSpecificFundraiserIds",
                            []
                          );
                        }
                        // Clear mobile page selection when switching away from mobile-page
                        if (e.target.value !== "mobile-page") {
                          updateBuilderField("redirectMobilePage", "");
                        }
                      }}
                    >
                      <option value={RedirectOtherFundraiserType.Gallery}>
                        Fundraiser gallery
                      </option>
                      <option value={RedirectOtherFundraiserType.ClosestGoal}>
                        Available fundraiser closest to goal
                      </option>
                      <option value={RedirectOtherFundraiserType.AnyActive}>
                        Any available fundraiser
                      </option>
                      <option value={RedirectOtherFundraiserType.Specific}>
                        Specific fundraisers
                      </option>
                      <option disabled>────────────────</option>
                      <option value={RedirectOtherFundraiserType.Website}>
                        Website URL
                      </option>
                      <option value={RedirectOtherFundraiserType.MobilePage}>
                        Mobile page
                      </option>
                    </select>
                    {builderData.redirectOtherFundraiserType ===
                      RedirectOtherFundraiserType.Specific && (
                        <button
                          type="button"
                          onClick={() => setShowFundraiserSelector(true)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "12px 16px",
                            height: "48px",
                            background: "transparent",
                            color: "#3b82f6",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Plus size={16} />
                          Add
                        </button>
                      )}
                  </div>

                  {/* Show selected fundraisers as badges */}
                  {builderData.redirectOtherFundraiserType ===
                    RedirectOtherFundraiserType.Specific &&
                    (builderData.redirectSpecificFundraiserIds || []).length >
                    0 && (
                      <SelectedBadgesContainer>
                        {(builderData.redirectSpecificFundraiserIds || []).map(
                          (fundraiserId: string) => {
                            const fundraiser = activeFundraisers.find(
                              (f) => f.id === fundraiserId
                            );
                            return fundraiser ? (
                              <SelectedBadge key={fundraiserId}>
                                {fundraiser.name}
                                <BadgeRemoveButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFundraiserFromSelection(fundraiserId);
                                  }}
                                >
                                  <X size={12} />
                                </BadgeRemoveButton>
                              </SelectedBadge>
                            ) : null;
                          }
                        )}
                      </SelectedBadgesContainer>
                    )}

                  {/* Show captions based on selection */}
                  {builderData.redirectOtherFundraiserType ===
                    RedirectOtherFundraiserType.AnyActive && (
                      <DropdownCaptionText>
                        Active fundraisers that have started will be randomly
                        selected
                      </DropdownCaptionText>
                    )}

                  {builderData.redirectOtherFundraiserType ===
                    RedirectOtherFundraiserType.ClosestGoal && (
                      <DropdownCaptionText>
                        Active fundraiser that has started and is closest to
                        achieving its goal will be selected. If no goals are
                        specified, an available fundraiser will be selected.
                      </DropdownCaptionText>
                    )}

                  {/* Website URL input */}
                  {builderData.redirectOtherFundraiserType ===
                    RedirectOtherFundraiserType.Website && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <input
                          style={{
                            flex: "1",
                            padding: "12px 16px",
                            border: `2px solid ${builderData.redirectWebsiteUrl && !isValidUrl(builderData.redirectWebsiteUrl) ? "#dc2626" : "#e2e8f0"}`,
                            borderRadius: "8px",
                            fontSize: "16px",
                            color: "#1e293b",
                            transition: "border-color 0.2s ease",
                            background: "#ffffff",
                          }}
                          type="text"
                          placeholder="Enter website URL..."
                          value={builderData.redirectWebsiteUrl || ""}
                          onChange={(e) =>
                            updateBuilderField(
                              "redirectWebsiteUrl",
                              e.target.value
                            )
                          }
                        />
                        <button
                          type="button"
                          onClick={handleWebsitePreview}
                          title="Preview URL"
                          disabled={
                            !builderData.redirectWebsiteUrl ||
                            !isValidUrl(builderData.redirectWebsiteUrl)
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            background: "none",
                            border: "none",
                            cursor:
                              builderData.redirectWebsiteUrl &&
                                isValidUrl(builderData.redirectWebsiteUrl)
                                ? "pointer"
                                : "not-allowed",
                            padding: "4px",
                            borderRadius: "4px",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity:
                              builderData.redirectWebsiteUrl &&
                                isValidUrl(builderData.redirectWebsiteUrl)
                                ? 1
                                : 0.3,
                          }}
                        >
                          <img src={PreviewIcon} alt="Preview" />
                        </button>
                      </div>
                    )}

                  {/* Mobile page selection */}
                  {builderData.redirectOtherFundraiserType ===
                    RedirectOtherFundraiserType.MobilePage && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <MobilePageDropdownContainer ref={mobilePageDropdownRef}>
                          <MobilePageButton
                            type="button"
                            isOpen={showMobilePageDropdown}
                            hasValue={!!builderData.redirectMobilePage}
                            onClick={() => {
                              setShowMobilePageDropdown(!showMobilePageDropdown);
                              if (!showMobilePageDropdown) {
                                setTimeout(() => {
                                  const searchInput = document.querySelector(
                                    "#mobile-page-search"
                                  ) as HTMLInputElement;
                                  if (searchInput) {
                                    searchInput.focus();
                                  }
                                }, 100);
                              }
                            }}
                          >
                            <span>
                              {builderData.redirectMobilePage
                                ? mobilePages.find(
                                  (p) => p.id === builderData.redirectMobilePage
                                )?.name || "Select mobile page..."
                                : "Select mobile page..."}
                            </span>
                            <ChevronIcon isOpen={showMobilePageDropdown} />
                          </MobilePageButton>

                          <MobilePageDropdownMenu isOpen={showMobilePageDropdown}>
                            <MobilePageSearchContainer>
                              <MobilePageSearchIcon />
                              <MobilePageSearchInput
                                id="mobile-page-search"
                                type="text"
                                placeholder="Search mobile pages..."
                                value={mobilePageSearchTerm}
                                onChange={(e) =>
                                  setMobilePageSearchTerm(e.target.value)
                                }
                              />
                              {mobilePageSearchTerm && (
                                <button
                                  type="button"
                                  onClick={clearMobilePageSearch}
                                  style={{
                                    position: "absolute",
                                    right: "24px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </MobilePageSearchContainer>

                            {getFilteredMobilePages().map((page) => (
                              <MobilePageOption
                                key={page.id}
                                type="button"
                                isSelected={
                                  builderData.redirectMobilePage === page.id
                                }
                                onClick={() => {
                                  updateBuilderField(
                                    "redirectMobilePage",
                                    page.id
                                  );
                                  setShowMobilePageDropdown(false);
                                  setMobilePageSearchTerm("");
                                }}
                              >
                                <span>{page.name}</span>
                                {builderData.redirectMobilePage === page.id && (
                                  <SelectedCheckmark>
                                    <Check size={12} />
                                  </SelectedCheckmark>
                                )}
                              </MobilePageOption>
                            ))}

                            {getFilteredMobilePages().length === 0 && (
                              <div
                                style={{
                                  padding: "16px",
                                  textAlign: "center",
                                  color: "#64748b",
                                  fontSize: "14px",
                                }}
                              >
                                No mobile pages found
                              </div>
                            )}
                          </MobilePageDropdownMenu>
                        </MobilePageDropdownContainer>

                        {builderData.redirectMobilePage && (
                          <button
                            type="button"
                            onClick={handleMobilePagePreview}
                            title="Preview mobile page"
                            disabled={!builderData.redirectMobilePage}
                            style={{
                              width: "32px",
                              height: "32px",
                              background: "none",
                              border: "none",
                              cursor: builderData.redirectMobilePage
                                ? "pointer"
                                : "not-allowed",
                              padding: "4px",
                              borderRadius: "4px",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: builderData.redirectMobilePage ? 1 : 0.3,
                            }}
                            onMouseEnter={(e) => {
                              if (builderData.redirectMobilePage) {
                                e.currentTarget.style.background = "#f1f5f9";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "none";
                            }}
                          >
                            <img src={PreviewIcon} alt="Preview" />
                          </button>
                        )}
                      </div>
                    )}
                </div>
              )}
          </div>
        </FormGroup>

        <div
          style={{ height: "1px", background: "#e2e8f0", margin: "32px 0" }}
        ></div>

        <FormGroup>
          <Label
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "15px",
            }}
          >
            Organize Donors
          </Label>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontWeight: "400" }}>Lists</Label>
          <div style={{ width: "50%" }}>
            <AdvancedDropdown
              placeholder="Add contact to list"
              options={fundraiserStore.lists.map((list) => {
                return {
                  value: list.id,
                  label: list.name,
                };
              })}
              selectedValues={
                builderData.selectedLists.map((list) => list.id) || []
              }
              onSelectionChange={(values) =>
                updateBuilderField(
                  "selectedLists",
                  fundraiserStore.lists.filter((list) =>
                    values.includes(list.id)
                  )
                )
              }
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontWeight: "400" }}>Interests</Label>
          <div style={{ width: "50%" }}>
            <AdvancedDropdown
              placeholder="Add contact to interest"
              options={fundraiserStore.interests.map((list) => {
                return {
                  value: list.id,
                  label: list.name,
                };
              })}
              selectedValues={
                builderData.selectedInterests.map((interest) => interest.id) ||
                []
              }
              onSelectionChange={(values) =>
                updateBuilderField(
                  "selectedInterests",
                  fundraiserStore.interests.filter((interest) =>
                    values.includes(interest.id)
                  )
                )
              }
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontWeight: "400" }}>Tags</Label>
          <div style={{ width: "50%" }}>
            <AdvancedDropdown
              placeholder="Add contact to tag"
              options={fundraiserStore.tags.map((list) => {
                return {
                  value: list.id,
                  label: list.name,
                };
              })}
              selectedValues={
                builderData.selectedTags.map((tag) => tag.id) || []
              }
              onSelectionChange={(values) =>
                updateBuilderField(
                  "selectedTags",
                  fundraiserStore.tags.filter((tag) => values.includes(tag.id))
                )
              }
            />
          </div>
        </FormGroup>

        {/* Fundraiser Selector Modal */}
        <FundraiserSelector
          isModal={true}
          isVisible={showFundraiserSelector}
          onClose={() => setShowFundraiserSelector(false)}
          selectedFundraiserIds={
            builderData.redirectSpecificFundraiserIds || []
          }
          onSelectionChange={handleFundraiserSelectionChange}
          excludeFundraiserId={builderData.id}
          status={[FundRaiserStatus.Active]}
          availability={["current", "future"]}
          multiple="yes"
        />
      </Section>
    );
  });
